import { useEffect, useRef, useCallback } from 'react';
import { useEntraAuth } from '../components/EntraAuth';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

export const useInactivityLogout = () => {
    const { isAuthenticated } = useEntraAuth();
    const timeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (isAuthenticated) {
            timeoutRef.current = setTimeout(() => {
                console.log('🔄 Auto-logout due to inactivity');

                // Clear overlay state from sessionStorage
                sessionStorage.removeItem('redirectOverlay');

                // Clear all storage
                localStorage.clear();
                sessionStorage.clear();

                // Clear MSAL cache
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('msal')) {
                        localStorage.removeItem(key);
                    }
                });

                // Redirect to evoomedical.ro with logout flag
                window.location.href = 'https://www.evoomedical.ro?logout=true';
            }, INACTIVITY_TIMEOUT);
        }
    }, [isAuthenticated]);

    const handleActivity = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            return;
        }

        // Set up event listeners for user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Start the timer initially
        resetTimer();

        // Cleanup function
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isAuthenticated, handleActivity, resetTimer]);

    // Return remaining time for optional UI display
    const getRemainingTime = useCallback(() => {
        if (!isAuthenticated || !timeoutRef.current) return 0;
        const elapsed = Date.now() - lastActivityRef.current;
        return Math.max(0, INACTIVITY_TIMEOUT - elapsed);
    }, [isAuthenticated]);

    return { getRemainingTime };
};
